import { StyledLink } from '@/components/Link'
import {
  useDefaultHeaderContent,
  getPendingJobScopeChangeElements,
  getRatingsElements,
  PageHeader,
  ScrolledAction,
} from '@/features/jobs/details/components/PageHeader'
import { type ReactNode } from 'react'
import type { JobDetailsViewModel } from '@/features/jobs/details/JobDetailsViewModel'
import { observer } from 'mobx-react-lite'
import {
  ExpectationsDialog,
  useExpectationsDialog,
} from '@/features/jobs/details/components/ExpectationsDialog'
import { Step, StepList } from '@/components/StepList'
import { ActionCard } from '@/features/jobs/details/components/ActionCard'
import { Button } from '@/components/Button'
import type { ConsultationJobDetailsViewModel } from '../../ConsultationJobDetailsViewModel'
import { TranscriptDownloadDropdownContainer } from '@/features/jobs/details/consultation/components/TranscriptDownloadDropdown'

export interface ConsultationJobDetailsHeaderProps {
  consultationVm: ConsultationJobDetailsViewModel
  jobVm: JobDetailsViewModel
}

/**
 * Job details header for Tax jobs specifically.
 */
export const ConsultationJobDetailsHeader = observer(
  function ConsultationJobDetailsHeader({
    consultationVm,
    jobVm,
  }: ConsultationJobDetailsHeaderProps) {
    const consultationDisplayInfo = getConsultationDisplayInfo(consultationVm)
    const { title, description } = useDefaultHeaderContent(jobVm, {
      consultation: consultationVm.consultation?.displayStage,
    })
    const { scrolledAction, actionCard } = getConsultationElements(
      jobVm,
      consultationDisplayInfo,
    )

    const {
      isOpen: isExpectationsDialogOpen,
      open: openExpectationsDialog,
      close: closeExpectationsDialog,
    } = useExpectationsDialog()

    return (
      <>
        <PageHeader
          vm={jobVm}
          headline={title}
          description={
            <>
              {description}{' '}
              <StyledLink onPress={openExpectationsDialog}>
                {jobVm.job.status === 'Closed' ? "What's done" : "What's next"}
              </StyledLink>
            </>
          }
          scrolledAction={scrolledAction}
          scrolledHeadline={title}
          actionCard={actionCard}
        />
        <ConsultationJobExpectationsDialog
          isOpen={isExpectationsDialogOpen}
          onClose={closeExpectationsDialog}
          vm={consultationVm}
        />
      </>
    )
  },
)

interface ConsultationJobExpectationsDialogProps {
  vm: ConsultationJobDetailsViewModel
  isOpen: boolean
  onClose: () => unknown
}

/**
 * Consultation Job Expectations Dialog
 */
export const ConsultationJobExpectationsDialog = observer(
  function ConsultationJobExpectationsDialog({
    vm,
    isOpen,
    onClose,
  }: ConsultationJobExpectationsDialogProps) {
    return (
      <ExpectationsDialog isOpen={isOpen} onClose={onClose}>
        <StepList>
          {vm.expectationSteps.map((step, index) => (
            <Step
              key={index}
              step={index + 1}
              title={step.title}
              state={
                step.isCompleted
                  ? 'completed'
                  : step.isCurrent
                    ? 'current'
                    : 'pending'
              }
            >
              {step.description}
            </Step>
          ))}
        </StepList>
      </ExpectationsDialog>
    )
  },
)

/**
 * Composes the action card for the consultation header
 */
function getConsultationElements(
  vm: JobDetailsViewModel,
  display: ConsultationDisplayInfo,
): {
  scrolledAction?: ReactNode
  actionCard?: ReactNode
} {
  const pendingJobScopeChangeElements = getPendingJobScopeChangeElements(vm)

  if (pendingJobScopeChangeElements) {
    return pendingJobScopeChangeElements
  }

  const ratingsElements = getRatingsElements(vm)

  if (ratingsElements) {
    return ratingsElements
  }

  return {
    actionCard: (
      <ActionCard kicker={display.kicker} title={display.title}>
        {display.action}
      </ActionCard>
    ),
    scrolledAction: display.scrolledAction,
  }
}

interface ConsultationDisplayInfo {
  kicker: string
  title: string
  action?: ReactNode
  scrolledAction?: ReactNode
}

/**
 * Gets the consultation display info based on the current stage
 */
function getConsultationDisplayInfo(
  vm: ConsultationJobDetailsViewModel,
): ConsultationDisplayInfo {
  const consultation = vm.consultation
  if (!consultation) {
    return {
      kicker: '',
      title: '',
    }
  }

  return vm.consultation.match<ConsultationDisplayInfo>({
    awaitingPickup: (stage) => ({
      kicker: 'Your upcoming appointment',
      title: 'Pending Pro pickup',
      action: vm.consultationManagerVm.isSettingAvailabilityAllowed && (
        <Button
          label={
            stage.availability.length > 0
              ? 'Edit availability'
              : 'Set availability'
          }
          onPress={() => vm.consultationManagerVm.showSetAvailabilityDialog()}
        />
      ),
      scrolledAction: vm.consultationManagerVm.isSettingAvailabilityAllowed && (
        <ScrolledAction
          label={
            stage.availability.length > 0
              ? 'Edit availability'
              : 'Set availability'
          }
          onPress={() => vm.consultationManagerVm.showSetAvailabilityDialog()}
        />
      ),
    }),
    awaitingProConfirmation: () => ({
      kicker: 'Your upcoming appointment',
      title: 'Pending Pro confirmation',

      action: (
        <Button
          label="View availability"
          variant="outline"
          onPress={vm.consultationManagerVm.showReviewAvailabilityDialog}
        />
      ),
      scrolledAction: (
        <ScrolledAction
          label="View availability"
          onPress={vm.consultationManagerVm.showReviewAvailabilityDialog}
        />
      ),
    }),
    booked: (stage) => ({
      kicker: 'Your upcoming appointment',
      title: stage.bookedTime.toStartingDayString(),
      action: (
        <Button
          label="Add to calendar"
          variant="outline"
          onPress={() =>
            vm.consultationManagerVm.downloadConsultationCalendar()
          }
          isPending={
            vm.consultationManagerVm.downloadConsultationCalendar.pending
          }
        />
      ),
      scrolledAction: (
        <ScrolledAction
          label="Add to calendar"
          onPress={() =>
            vm.consultationManagerVm.downloadConsultationCalendar()
          }
          isDisabled={
            vm.consultationManagerVm.downloadConsultationCalendar.pending
          }
        />
      ),
    }),
    dayOfCall: (stage) => ({
      kicker: 'Your upcoming appointment',
      title: `Today at ${stage.bookedTime.toStartingHourString()}`,
      action: (
        <Button
          label="Join call"
          onPress={() => vm.consultationManagerVm.joinRoom()}
          isPending={vm.consultationManagerVm.joinRoom.pending}
        />
      ),
      scrolledAction: (
        <ScrolledAction
          label="Join call"
          onPress={() => vm.consultationManagerVm.joinRoom()}
          isDisabled={vm.consultationManagerVm.joinRoom.pending}
        />
      ),
    }),
    inProgress: () => ({
      kicker: 'Your upcoming appointment',
      title: 'In progress',
      action: (
        <Button
          label="Join call"
          onPress={() => vm.consultationManagerVm.joinRoom()}
          isPending={vm.consultationManagerVm.joinRoom.pending}
        />
      ),
      scrolledAction: (
        <ScrolledAction
          label="Join call"
          onPress={() => vm.consultationManagerVm.joinRoom()}
        />
      ),
    }),
    callCompleted: (stage) => {
      const isTranscriptAvailable = Boolean(
        vm.consultationManagerVm.transcriptVms?.length,
      )
      return {
        kicker: 'Call completed',
        title: stage.bookedTime.toStartingDayString(),
        action: isTranscriptAvailable && (
          <TranscriptDownloadDropdownContainer
            vm={vm.consultationManagerVm}
            variant="button"
          />
        ),
        scrolledAction: isTranscriptAvailable && (
          <TranscriptDownloadDropdownContainer
            vm={vm.consultationManagerVm}
            variant="scrolled-action"
          />
        ),
      }
    },
    rescheduleNeeded: () => ({
      kicker: 'Your appointment expired',
      title: 'Reschedule your appointment',
      action: (
        <Button
          variant="text-inline"
          label="Update availability"
          onPress={() => vm.consultationManagerVm.showSetAvailabilityDialog()}
          testId="update-availability-button"
        />
      ),
    }),
  })
}
